import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type AnalyticsPeriod = 7 | 30 | 90

export type RevenueBucket = {
  date: string
  label: string
  revenue: number
}

export type StoreAnalyticsOverview = {
  storeId: number | null
  currency: string
  period: AnalyticsPeriod
  periodStart: string
  periodEnd: string
  totalRevenue: number
  totalOrders: number
  revenueOverTime: {
    granularity: 'day'
    days: number
    buckets: RevenueBucket[]
  }
  ordersByStatus: { status: string; count: number }[]
  topProducts: {
    productId: number
    name: string
    slug: string
    sku: string
    unitsSold: number
    revenue: number
  }[]
}

export function periodToIsoRange(period: AnalyticsPeriod): { from: string; to: string } {
  const end = new Date()
  const start = new Date()
  start.setUTCDate(start.getUTCDate() - (period - 1))
  start.setUTCHours(0, 0, 0, 0)
  return { from: start.toISOString(), to: end.toISOString() }
}

export function isoDateInputValue(iso: string): string {
  return iso.slice(0, 10)
}

export function dateInputToIsoStart(date: string): string {
  return `${date}T00:00:00.000Z`
}

export function dateInputToIsoEnd(date: string): string {
  return `${date}T23:59:59.999Z`
}

export async function fetchAnalyticsOverview(params: {
  period?: AnalyticsPeriod
  storeId?: number
}): Promise<StoreAnalyticsOverview> {
  try {
    const { data } = await api.get('/storebusiness/analytics/overview', {
      params: {
        period: params.period ?? 30,
        ...(params.storeId != null ? { storeId: params.storeId } : {}),
      },
    })
    return assertSuccess(data) as StoreAnalyticsOverview
  } catch (e) {
    throw apiError(e, 'Failed to load analytics')
  }
}

async function downloadCsv(path: string, params: Record<string, string | number | undefined>, filename: string) {
  const { data } = await api.get(path, {
    params,
    responseType: 'blob',
  })
  const blob = data as Blob
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export async function exportOrdersCsv(params: {
  from: string
  to: string
  storeId?: number
}) {
  try {
    await downloadCsv(
      '/storebusiness/analytics/exports/orders',
      {
        from: params.from,
        to: params.to,
        ...(params.storeId != null ? { storeId: params.storeId } : {}),
      },
      'orders.csv',
    )
  } catch (e) {
    throw apiError(e, 'Failed to export orders')
  }
}

export async function exportTransactionsCsv(params: {
  from: string
  to: string
  storeId?: number
}) {
  try {
    await downloadCsv(
      '/storebusiness/analytics/exports/transactions',
      {
        from: params.from,
        to: params.to,
        ...(params.storeId != null ? { storeId: params.storeId } : {}),
      },
      'transactions.csv',
    )
  } catch (e) {
    throw apiError(e, 'Failed to export transactions')
  }
}
